import { useState } from 'react';
import MetricCard from '../MetricCard';
import ChartCard from '../charts/ChartCard';
import CustomTooltip from '../charts/CustomTooltip';
import { formatNumber } from '../../utils/formatters';
import { CHART_COLORS, CHART_PALETTE, CHART_THEME } from '../../utils/chartColors';
import {
  FiUsers,
  FiAward,
  FiEdit,
  FiHelpCircle
} from 'react-icons/fi';
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const SourcesTab = ({ data }) => {
  const [sourcePage, setSourcePage] = useState(0);
  const [sortBy, setSortBy] = useState('activeCount'); // 'activeCount' or 'mrr'
  const sourcesPerPage = 10;

  if (!data) return null;

  const { sources } = data;
  const breakdown = sources?.breakdown || [];
  const sourceTimeSeries = sources?.sourceTimeSeries || [];

  // Top 15 for bar charts
  const top15ByCount = [...breakdown].sort((a, b) => b.activeCount - a.activeCount).slice(0, 15);
  const top15ByMrr = [...breakdown].sort((a, b) => b.mrr - a.mrr).slice(0, 15);

  // Source distribution donut (top 5 + others)
  const top5 = breakdown.slice(0, 5);
  const othersCount = breakdown.slice(5).reduce((sum, s) => sum + s.activeCount, 0);
  const donutData = [
    ...top5.map(s => ({ name: s.source, value: s.activeCount })),
    ...(othersCount > 0 ? [{ name: 'Others', value: othersCount }] : [])
  ];

  // Source time series for stacked area
  const top5Names = top5.map(s => s.source);
  const timeSeriesData = sourceTimeSeries.map(ts => ({
    month: ts.month,
    ...ts.sources
  }));

  // Sorted breakdown for table
  const sortedBreakdown = [...breakdown].sort((a, b) => b[sortBy] - a[sortBy]);
  const paginatedBreakdown = sortedBreakdown.slice(
    sourcePage * sourcesPerPage,
    (sourcePage + 1) * sourcesPerPage
  );
  const totalPages = Math.ceil(sortedBreakdown.length / sourcesPerPage);

  return (
    <div className="space-y-6">
      {/* Metric Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          label="Total Sources"
          value={formatNumber(sources.totalSources)}
          icon={FiUsers}
        />
        <MetricCard
          label="Top Source"
          value={sources.topSource?.name || 'None'}
          subtitle={sources.topSource ? `${sources.topSource.count} subs · $${formatNumber(sources.topSource.mrr)}/mo` : ''}
          icon={FiAward}
        />
        <MetricCard
          label="Overridden"
          value={formatNumber(sources.overriddenCount)}
          subtitle="Manual source overrides"
          icon={FiEdit}
        />
        <MetricCard
          label="Unknown"
          value={formatNumber(sources.unknownCount)}
          subtitle="Unattributed subscriptions"
          icon={FiHelpCircle}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Source by Count */}
        <ChartCard title="Sources by Subscription Count" subtitle="Top 15 sources by active subscriptions">
          <ResponsiveContainer width="100%" height={Math.max(280, top15ByCount.length * 28)}>
            <BarChart data={top15ByCount} layout="vertical" barSize={16}>
              <CartesianGrid {...CHART_THEME.grid} horizontal={false} />
              <XAxis type="number" tick={CHART_THEME.axis} />
              <YAxis type="category" dataKey="source" tick={{ ...CHART_THEME.axis, fontSize: 10 }} width={120} />
              <Tooltip content={<CustomTooltip formatter={(val) => `${val} subscriptions`} />} />
              <Bar dataKey="activeCount" fill={CHART_COLORS.primary} name="Active Subs" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Source by MRR */}
        <ChartCard title="Sources by MRR" subtitle="Top 15 sources by monthly recurring revenue">
          <ResponsiveContainer width="100%" height={Math.max(280, top15ByMrr.length * 28)}>
            <BarChart data={top15ByMrr} layout="vertical" barSize={16}>
              <CartesianGrid {...CHART_THEME.grid} horizontal={false} />
              <XAxis type="number" tick={CHART_THEME.axis} tickFormatter={(v) => `$${v.toLocaleString()}`} />
              <YAxis type="category" dataKey="source" tick={{ ...CHART_THEME.axis, fontSize: 10 }} width={120} />
              <Tooltip content={<CustomTooltip formatter={(val) => `$${val.toLocaleString()}/mo`} />} />
              <Bar dataKey="mrr" fill={CHART_COLORS.success} name="MRR" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Source Performance Over Time */}
        <ChartCard title="Source Performance Over Time" subtitle="Monthly new subscriptions by top 5 sources">
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={timeSeriesData}>
              <CartesianGrid {...CHART_THEME.grid} />
              <XAxis dataKey="month" tick={CHART_THEME.axis} />
              <YAxis tick={CHART_THEME.axis} />
              <Tooltip content={<CustomTooltip />} />
              <Legend iconType="circle" iconSize={8} />
              {top5Names.map((name, i) => (
                <Area
                  key={name}
                  type="monotone"
                  dataKey={name}
                  stackId="1"
                  stroke={CHART_PALETTE[i]}
                  fill={CHART_PALETTE[i]}
                  fillOpacity={0.4}
                  name={name}
                />
              ))}
              <Area
                type="monotone"
                dataKey="Others"
                stackId="1"
                stroke={CHART_COLORS.muted}
                fill={CHART_COLORS.muted}
                fillOpacity={0.3}
                name="Others"
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Source Distribution Donut */}
        <ChartCard title="Source Distribution" subtitle="Share of subscriptions by source">
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={donutData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={90}
                paddingAngle={3}
                strokeWidth={0}
              >
                {donutData.map((entry, i) => (
                  <Cell key={i} fill={i < CHART_PALETTE.length ? CHART_PALETTE[i] : CHART_COLORS.muted} />
                ))}
              </Pie>
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0];
                  const total = donutData.reduce((s, item) => s + item.value, 0);
                  return (
                    <div className="bg-slate-800 text-slate-50 px-3 py-2 rounded-lg shadow-lg text-xs">
                      <p className="font-medium">{d.name}</p>
                      <p className="text-slate-300">{d.value} subs ({((d.value / total) * 100).toFixed(1)}%)</p>
                    </div>
                  );
                }}
              />
              <Legend iconType="circle" iconSize={8} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Full Source Table */}
      {breakdown.length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-700">All Sources</h3>
            <div className="flex gap-2">
              <button
                onClick={() => { setSortBy('activeCount'); setSourcePage(0); }}
                className={`text-xs px-3 py-1.5 rounded-md transition-colors ${sortBy === 'activeCount' ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-500 hover:text-gray-700'}`}
              >
                By Count
              </button>
              <button
                onClick={() => { setSortBy('mrr'); setSourcePage(0); }}
                className={`text-xs px-3 py-1.5 rounded-md transition-colors ${sortBy === 'mrr' ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-500 hover:text-gray-700'}`}
              >
                By MRR
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">Source</th>
                  <th className="text-right py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">Active Subs</th>
                  <th className="text-right py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">MRR</th>
                  <th className="text-right py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">% of Subs</th>
                  <th className="text-right py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">% of MRR</th>
                  <th className="text-center py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">Override</th>
                </tr>
              </thead>
              <tbody>
                {paginatedBreakdown.map((s) => (
                  <tr key={s.source} className="border-b border-gray-50 hover:bg-gray-50/50">
                    <td className="py-3">
                      <span className="text-sm font-medium text-gray-900">{s.source}</span>
                    </td>
                    <td className="text-right py-3 text-sm text-gray-900 tabular-nums">{s.activeCount}</td>
                    <td className="text-right py-3 text-sm font-medium text-gray-900 tabular-nums">${formatNumber(s.mrr)}/mo</td>
                    <td className="text-right py-3 text-sm text-gray-600 tabular-nums">{s.pctOfSubs}%</td>
                    <td className="text-right py-3 text-sm text-gray-600 tabular-nums">{s.pctOfMrr}%</td>
                    <td className="text-center py-3">
                      {s.isOverridden ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700">Yes</span>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
              <button
                onClick={() => setSourcePage(p => Math.max(0, p - 1))}
                disabled={sourcePage === 0}
                className="text-sm text-gray-600 hover:text-gray-900 disabled:text-gray-300 disabled:cursor-not-allowed"
              >
                ← Previous
              </button>
              <span className="text-xs text-gray-500">
                {sourcePage + 1} of {totalPages} · {sortedBreakdown.length} sources
              </span>
              <button
                onClick={() => setSourcePage(p => Math.min(totalPages - 1, p + 1))}
                disabled={sourcePage >= totalPages - 1}
                className="text-sm text-gray-600 hover:text-gray-900 disabled:text-gray-300 disabled:cursor-not-allowed"
              >
                Next →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SourcesTab;
